import PropTypes from "prop-types";
import Icon from "@react-native-vector-icons/material-design-icons";
import theme from "assets/theme";
import React, { useContext, useMemo } from "react";
import { ActivityIndicator, StyleSheet, Text, View } from "react-native";
import PressableContainer from "webComponentsFormElements/PressableContainer.component";
import { OrganizationContext } from "webContext/Organization.context";

const classes = StyleSheet.create({
  root: {
    width: "max-content",
  },
  textContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
  },
  buttonText: {
    color: theme.color.grey,
    fontSize: Math.min(theme.font.size.small, 15),
    fontWeight: theme.font.weight.semibold,
  },
  dangerButton: {
    backgroundColor: theme.color.red,
    borderRadius: theme.card.border.radius,
    justifyContent: "center",
    alignItems: "center",
    marginTop: 10,
    marginBottom: 10,
    padding: 10,
  },
  cancelButton: {
    backgroundColor: theme.color.darkGrey,
    borderRadius: theme.card.border.radius,
    justifyContent: "center",
    alignItems: "center",
    marginTop: 10,
    marginBottom: 10,
    padding: 10,
  },
  cancelButtonText: {
    color: theme.color.white,
    fontSize: Math.min(theme.font.size.small, 15),
    fontWeight: theme.font.weight.semibold,
  },
  disabledButton: {
    backgroundColor: theme.color.disabled,
    borderRadius: theme.card.border.radius,
    justifyContent: "center",
    alignItems: "center",
    marginTop: 10,
    marginBottom: 10,
    padding: 10,
  },
});

export default function CustomButton(props) {
  const {
    title,
    icon,
    isIconSvg,
    onPress,
    variant,
    disabled,
    selected,
    isLoading,
    iconColor: iconColorProp,
    iconSizeOverride,
    styleOverride,
    testID,
  } = props;

  const { organizationColor } = useContext(OrganizationContext);

  const stateClasses = useMemo(
    () =>
      StyleSheet.create({
        button: {
          borderRadius: theme.card.border.radius,
          backgroundColor: organizationColor,
          justifyContent: "center",
          alignItems: "center",
          marginTop: 10,
          marginBottom: 10,
          boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.2)",
          elevation: 2,
          padding: 10,
        },
        buttonLightText: {
          color: selected ? theme.color.white : organizationColor,
          fontSize: Math.min(theme.font.size.small, 15),
          fontWeight: theme.font.weight.semibold,
        },
        buttonLight: {
          borderRadius: theme.card.border.radius,
          borderWidth: theme.card.border.width * 0.3,
          justifyContent: "center",
          alignItems: "center",
          borderColor: organizationColor,
          marginTop: 10,
          marginBottom: 10,
          backgroundColor: theme.color.background,
          boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.2)",
          elevation: 2,
          padding: 10,
        },
        iconOnly: {
          backgroundColor: theme.color.background,
          justifyContent: "center",
          alignItems: "center",
          marginTop: 10,
          marginBottom: 10,
          padding: 10,
          buttonSize: 28,
        },
        selectedButton: {
          backgroundColor: organizationColor,
          borderRadius: theme.card.border.radius,
          justifyContent: "center",
          alignItems: "center",
          marginTop: 10,
          marginBottom: 10,
          padding: 10,
        },
      }),
    [organizationColor, selected],
  );

  const buttonStyle = useMemo(() => {
    if (disabled) {
      return classes.disabledButton;
    } else if (selected) {
      return stateClasses.selectedButton;
    } else if (variant === "dark") {
      return stateClasses.button;
    } else if (variant === "danger") {
      return classes.dangerButton;
    } else if (variant === "cancel") {
      return classes.cancelButton;
    } else if (variant === "icon") {
      return stateClasses.iconOnly;
    } else {
      return stateClasses.buttonLight;
    }
  }, [disabled, variant, stateClasses, selected]);

  return (
    <PressableContainer
      rootStyle={[classes.root, styleOverride]}
      style={[buttonStyle, styleOverride]}
      onPress={onPress}
      disabled={disabled}
      testID={testID}
    >
      {isLoading ? (
        <View>
          <ActivityIndicator color="#fff" />
        </View>
      ) : (
        <View style={classes.textContainer}>
          {icon && !isIconSvg && (
            <Icon
              name={icon}
              size={iconSizeOverride || classes.buttonText.fontSize}
              color={
                iconColorProp ||
                (variant === "cancel"
                  ? theme.color.white
                  : variant === "dark" || variant === "danger" || disabled
                    ? classes.buttonText.color
                    : stateClasses.buttonLightText.color)
              }
            />
          )}
          {isIconSvg && icon}
          <Text
            style={
              variant === "cancel"
                ? classes.cancelButtonText
                : variant === "dark" || variant === "danger" || disabled
                  ? classes.buttonText
                  : stateClasses.buttonLightText
            }
          >
            {title}
          </Text>
        </View>
      )}
    </PressableContainer>
  );
}

  CustomButton.propTypes = {
    title: PropTypes.string.isRequired,
    onPress: PropTypes.func,
    disabled: PropTypes.bool,
    testID: PropTypes.string,
    variant: PropTypes.oneOf(["dark", "danger", "cancel", "icon", "light"]),
    isLoading: PropTypes.bool,
    icon: PropTypes.string,
    isIconSvg: PropTypes.bool,
    iconSizeOverride: PropTypes.number,
    styleOverride: PropTypes.object,
  };